import { Provider, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import { useEffect, useMemo, useRef, useState } from 'react';

import { Layout } from '@poliedro/tamentai';

import { useQuery } from '@tanstack/react-query';

import Header from './components/Header';
import AccessUnavailableDialog from './components/molecules/AccessUnavailableDialog';
import NavBar from './components/NavBar';
import { useBootstrapFeatures } from './hooks/useBootstrapFeatures';
import { useToken } from './hooks/useToken';
import ErrorPage from './pages/ErrorPage';
import CSATFeature from './pages/Home/components/CSAT/CSATFeature';
import InitialPage from './pages/InitialPage';
import AppRoutes from './routes';
import { checkCSAT, ignoreCSAT, respondCSATFeature } from './services/CSAT';
import { initMqttClient } from './services/mqtt/index';
import { getMqttClient } from './services/mqtt/index';
import { useProfileData } from './services/useProfileData';
import { persistor, RootState, store } from './store';
import { AllowedProfiles } from './utils/AllowedProfiles';

function AppContent() {
  const [isCSATFeatureOpen, setIsCSATFeatureOpen] = useState(false);
  const isSubmittingCSAT = useRef(false);
  const profile = useSelector((state: RootState) => state.profile.profileData);
  const location = useLocation();
  const isErrorPage = location.pathname === '/error';
  const { userData } = useProfileData(!isErrorPage);

  useToken({ enabled: !isErrorPage });
  useBootstrapFeatures();

  const shouldShowAccessUnavailableDialog =
    AllowedProfiles(profile?.perfil?.nome, ['Professor', 'Coordenador']) &&
    (userData?.classes?.length === 0 ||
      (userData?.classes?.length === 1 &&
        userData.classes[0]?.active_class === false));

  useEffect(() => {
    if (!profile?.id) return;
    initMqttClient();
  }, [profile?.id]);

  const csatFeatureName = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    const form = searchParams.get('form');

    if (location.pathname === '/pei') return 'pei';

    if (location.pathname === '/chat') {
      if (form === 'avaliacao') return 'CDIA';

      if (form === 'plano-de-aula') return 'planoDeAula';
    }

    return null;
  }, [location.pathname, location.search]);

  const {
    data: csatCheckData,
    isSuccess: isCSATCheckSuccess,
    isFetching: isCheckingCSAT,
  } = useQuery({
    queryKey: ['checkCSAT', profile?.id, csatFeatureName],
    queryFn: () =>
      checkCSAT({
        user_id: profile!.id,
        feature_name: csatFeatureName,
      }),
    enabled: !!profile?.id && !!csatFeatureName && !isErrorPage,
    retry: 1,
  });

  const shouldShowCSATFeature =
    !!csatFeatureName &&
    isCSATCheckSuccess &&
    !isCheckingCSAT &&
    csatCheckData?.show_csat === true;

  useEffect(() => {
    setIsCSATFeatureOpen(shouldShowCSATFeature);
  }, [shouldShowCSATFeature]);

  const getCSATFeatureConfig = (featureName: string | null) => {
    const configs: Record<string, { label: string; questionPrefix: string }> = {
      pei: {
        label: 'PEI+',
        questionPrefix: 'O que está achando do',
      },
      CDIA: {
        label: 'Avaliação Descomplicada',
        questionPrefix: 'O que está achando da',
      },
      planoDeAula: {
        label: 'Plano de Aula',
        questionPrefix: 'O que está achando do',
      },
    };

    return (
      configs[featureName ?? ''] ?? {
        label: '',
        questionPrefix: 'O que está achando da',
      }
    );
  };

  const csatFeatureConfig = getCSATFeatureConfig(csatFeatureName);

  if (isErrorPage) {
    return <ErrorPage />;
  }

  if (!profile) {
    return <InitialPage />;
  }

  return (
    <Layout.Root logo={''} primaryColor="#005FFB">
      <AccessUnavailableDialog open={shouldShowAccessUnavailableDialog} />
      <Layout.Navbar>
        <Layout.Close />
        <Layout.Profile>
          <Layout.Text text={profile?.nomeEscola ?? ' '} variant="title" />
          <Layout.Text text={profile?.enderecoEscola ?? ''} />
          <Layout.Text text={profile?.segmentos?.join(' - ') ?? ''} />
        </Layout.Profile>
        <NavBar />
      </Layout.Navbar>
      <Layout.Content>
        <Header
          name={profile?.nome ?? ''}
          userProfile={profile?.perfil.nome ?? ''}
        />
        <Layout.Body>
          <AppRoutes />
        </Layout.Body>
      </Layout.Content>
      {shouldShowCSATFeature && (
        <CSATFeature
          open={isCSATFeatureOpen}
          setOpen={setIsCSATFeatureOpen}
          featureName={csatFeatureConfig.label}
          questionPrefix={csatFeatureConfig.questionPrefix}
          onSubmit={payload => {
            if (!profile?.id || !csatFeatureName) return;
            isSubmittingCSAT.current = true;
            respondCSATFeature({
              user_id: profile.id,
              feature_name: csatFeatureName,
              rating: payload.rating,
              feedback: payload.feedback,
              contact: payload.contact,
            });
            setIsCSATFeatureOpen(false);
          }}
          onCancel={() => {
            if (isSubmittingCSAT.current) {
              isSubmittingCSAT.current = false;
              return;
            }
            if (!profile?.id || !csatFeatureName) return;

            ignoreCSAT({
              user_id: profile.id,
              feature_name: csatFeatureName,
            });
          }}
        />
      )}
    </Layout.Root>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  );
}

export default App;
